import {
  ReactElement,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import useAnswerRules from "../../../hooks/useAnswerRules";
import { AnswerType, AnswerValueType } from "../../../types/Answer";
import { QuestionType } from "../../../types/Question";
import { QuestionnaireSession } from "../../../types/Questionnaire";
import Question from "../../molecules/Question/Question";
import Container from "../../atoms/Container";
import NextPrevButtons from "../../molecules/NextPrevButtons";
import useVocalisation from "../../../hooks/useVocalisation";
import FinishedQuestionnaire from "../../../pages/FinishedQuestionnaires/FinishedQuestionnaire";
import {
  calculateQuestionnaireScore,
  getNextQuestion,
  isQuestionnaireDone,
} from "../../../helper/questionnaire";
import useToast from "../../../hooks/useToast";
import { useResonanceLayout } from "../ResonanceContext/ResonanceLayoutContext";
import styled from "styled-components";
import ResonanceLayout from "../ResonanceContext/ResonanceLayout";
import { useIonViewWillEnter, useIonViewWillLeave } from "@ionic/react";
import React from "react";

interface DisplayQuestionsProps {
  questionnaireTitle: string;
  session: QuestionnaireSession;
  onSessionChange: (
    updater: QuestionnaireSession | ((prev: QuestionnaireSession) => QuestionnaireSession)
  ) => void;
  onSubmitAnswer: (question: QuestionType, answer: AnswerType) => Promise<void>;
  onQuestionnaireDone?: (totalScore: number | null) => void;
  onNextClick(): void;
  onPrevClick(): void;
}

const Questionnaire = ({
  questionnaireTitle,
  session,
  onSessionChange,
  onSubmitAnswer,
  onQuestionnaireDone,
  onNextClick,
  onPrevClick,
}: DisplayQuestionsProps): ReactElement => {
  const { questions, answers, history, dependencies } = session;
  const { setResonanceLayout } = useResonanceLayout();

  useEffect(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: true,
      backgroundColor: "white",
      title: questionnaireTitle,
    });

    // return () =>
    //   setResonanceLayout({
    //     disableHeader: false,
    //     disableNav: false,
    //     backgroundColor: "white",
    //     title: questionnaireTitle,
    //   });
  }, []);

  const currentQuestion: QuestionType = history[history.length - 1];
  const present = useToast();

  const currentAnswer: AnswerType | undefined = useMemo(
    () => answers.find((ans) => ans.questionId === currentQuestion.id),
    [answers, currentQuestion.id]
  );

  const [isFinishedQuestionnaire, setIsFinishedQuestionnaire] = useState(false);

  const { translatedRuleMsg, isAnswerValid } = useAnswerRules(
    currentQuestion,
    currentAnswer?.value
  );

  const changeAnswer = useCallback(
    (answer: AnswerValueType): void => {
      onSessionChange((prev) => {
        const newAnswers = prev.answers.map((a) =>
          a.questionId === currentQuestion.id
            ? {
                ...a,
                value:
                  currentAnswer?.questionType === "SLIDER" && answer === null
                    ? 0
                    : answer,
              }
            : a
        );
        return { ...prev, answers: newAnswers };
      });
    },
    [currentQuestion.id, currentAnswer?.questionType, onSessionChange]
  );

  const goToPrevQuestion = useCallback((): void => {
    let sliceValue = -1;
    let choseAnswerWithDependency = null;

    if (dependencies && dependencies.length) {
      const possiblePreviousQuestions = dependencies.filter((dependency) =>
        dependency.answers?.find(
          (answer) =>
            answer.next_question &&
            answer.next_question.id === currentQuestion.id
        )
      );

      if (possiblePreviousQuestions.length) {
        const mostRecentAnsweredQuestion = possiblePreviousQuestions.reduce(
          (max, obj) => (obj.order > max.order ? obj : max)
        );
        const previousQuestionIndex = history.findIndex(
          (item) => item.id === mostRecentAnsweredQuestion.id
        );

        choseAnswerWithDependency = mostRecentAnsweredQuestion.answers.find(
          (answer) =>
            answers.some(
              (sessionAnswer) =>
                sessionAnswer.value === answer.id &&
                sessionAnswer.questionId === mostRecentAnsweredQuestion.id &&
                answer.next_question.id === currentQuestion.id
            )
        );

        if (choseAnswerWithDependency) {
          sliceValue = previousQuestionIndex - history.length + 1;
        }
      }
    }

    if (history.length > 1) {
      onSessionChange((prev) => ({
        ...prev,
        history: history.slice(0, sliceValue),
      }));
    } else {
      onPrevClick();
    }
  }, [dependencies, currentQuestion.id, history, answers, onSessionChange, onPrevClick]);

  const { readText } = useVocalisation();

  const goToNextQuestion = useCallback(async (): Promise<void> => {
    try {
      if (!currentAnswer) return;
      await onSubmitAnswer(currentQuestion, currentAnswer);

      const nextQuestion = getNextQuestion(
        questions,
        dependencies,
        currentAnswer
      );

      const done = isQuestionnaireDone(dependencies, answers);

      if (!nextQuestion || done) {
        setResonanceLayout({});
        const total_score = calculateQuestionnaireScore(
          questions,
          answers,
          session.is_scored
        );
        setIsFinishedQuestionnaire(true);
        onQuestionnaireDone?.(total_score);
        return;
      }

      onSessionChange((prev) => ({
        ...prev,
        history: [...prev.history, nextQuestion],
      }));
    } catch (e) {
      console.log(e);
      present(e?.response?.data?.message);
    }
  }, [
    answers,
    currentAnswer,
    currentQuestion,
    dependencies,
    onSessionChange,
    onSubmitAnswer,
    onQuestionnaireDone,
    present,
    questions,
    session.is_scored,
    setResonanceLayout,
  ]);

  const isNextDisabled = !isAnswerValid;
  const progressPercent = Math.round((history.length / questions.length) * 100);

  if (isFinishedQuestionnaire) {
    return <FinishedQuestionnaire onClick={onNextClick} />;
  }

  console.log(
    "[Questionnaire] rendering with question:",
    currentQuestion?.id,
    "progress:",
    progressPercent
  );

  return (
      <Container>
        <Progress percent={progressPercent} />

        <MemoizedQuestion
          ruleMessage={translatedRuleMsg}
          answer={currentAnswer?.value}
          question={currentQuestion}
          onAnswerChange={changeAnswer}
          readText={readText}
        />

        <MemoizedNextPrevButtons
          disableNext={isNextDisabled}
          onPreviousClick={goToPrevQuestion}
          onNextClick={goToNextQuestion}
        />
      </Container>
  );
};

/** 🔹 Memoized Progress Component */
const Progress = React.memo(({ percent }: { percent: number }) => (
  <ProgressContainer>
    <ProgressLabel>{percent}%</ProgressLabel>
    <ProgressBarWrapper>
      <ProgressFill $width={percent} />
    </ProgressBarWrapper>
  </ProgressContainer>
));

/** 🔹 Memoized children */
const MemoizedQuestion = React.memo(Question);
const MemoizedNextPrevButtons = React.memo(NextPrevButtons);

const ProgressContainer = styled.div`
  width: 100%;
  padding: 10px 16px;
`;

const ProgressLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  text-align: right;
  margin-bottom: 4px;
`;

const ProgressBarWrapper = styled.div`
  background-color: #e6e6e6;
  border-radius: 10px;
  height: 12px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $width?: number }>`
  background: linear-gradient(
    270deg,
    #2db6b4 2.69%,
    #2db6b4 2.7%,
    #abf0ef 105.65%
  );
  height: 100%;
  border-radius: 10px;
  width: ${(props) => props.$width}%;
  transition: width 0.4s ease-in-out;
`;

export default Questionnaire;
